import { useCallback, useEffect, useMemo, useState } from 'react';
import './App.css';
import CardsList from './components/CardsList/CardsList';
import SearchBar from './components/SearchBar/SearchBar';
import { Game, Region } from './interfaces/GameInterface';
import { API_URL } from './config';

type Status = 'loading' | 'ready' | 'error';

const MAX_RETRIES = 5;

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

function App() {
  const [games, setGames] = useState<Game[]>([]);
  const [status, setStatus] = useState<Status>('loading');
  const [searchTerm, setSearchTerm] = useState('');
  const [region, setRegion] = useState<Region | 'all'>('all');

  // Bounded, backing-off fetch. The API cold-starts and occasionally returns
  // an errorMessage / non-200; we retry a few times instead of looping forever.
  const fetchGames = useCallback(async () => {
    setStatus('loading');
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const res = await fetch(API_URL, { method: 'GET' });
        const data = await res.json();
        if (data && data.statusCode === 200 && Array.isArray(data.body)) {
          setGames(data.body);
          setStatus('ready');
          return;
        }
      } catch {
        /* network hiccup — fall through to retry */
      }
      await wait(600 * (attempt + 1)); // linear backoff
    }
    setStatus('error');
  }, []);

  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  const regionCounts = useMemo(() => {
    const c = { na: 0, eu: 0, jp: 0 };
    for (const g of games) {
      if (g.usa_released) c.na++;
      if (g.eu_released) c.eu++;
      if (g.jp_released) c.jp++;
    }
    return c;
  }, [games]);

  return (
    <div className="app">
      <header className="header">
        <div className="brand">
          <span className="brand-mark">VITA</span>
          <span className="brand-rest">Games&nbsp;DB</span>
        </div>
        <p className="tagline">
          PS&nbsp;Vita Library
          {status === 'ready' && (
            <> - <strong>{games.length.toLocaleString()}</strong> Games</>
          )}
        </p>
      </header>

      {status === 'ready' && (
        <SearchBar
          value={searchTerm}
          onSearch={setSearchTerm}
          region={region}
          onRegionChange={setRegion}
          regionCounts={regionCounts}
        />
      )}

      <main className="content">
        {status === 'loading' && (
          <div className="state">
            <div className="spinner" aria-label="Loading" />
            <p>Loading the Vita library…</p>
          </div>
        )}

        {status === 'error' && (
          <div className="state">
            <p className="state-title">Couldn’t reach the games API</p>
            <p className="state-sub">The service may be cold-starting.</p>
            <button className="retry" onClick={fetchGames}>
              Try again
            </button>
          </div>
        )}

        {status === 'ready' && (
          <CardsList games={games} searchTerm={searchTerm} region={region} />
        )}
      </main>

      <footer className="footer">
        Data &amp; cover art from vitagamesdb.com · A fan-made database
      </footer>
    </div>
  );
}

export default App;
